import api from './api';

// =====================================================
// TYPES - CMS
// =====================================================

export interface CMSHeroAvatar {
  id: number;
  imagem_url: string;
  nome?: string;
  ordem: number;
}

export interface CMSHero {
  id: number;
  badge_texto?: string;
  titulo: string;
  subtitulo?: string;
  descricao?: string;
  btn_primario_texto?: string;
  btn_primario_link?: string;
  btn_primario_icone?: string;
  btn_secundario_texto?: string;
  btn_secundario_link?: string;
  btn_secundario_icone?: string;
  trust_badge_texto?: string;
  trust_badge_rating?: number;
  imagem_fundo?: string;
  avatars?: CMSHeroAvatar[];
}

export interface CMSFeature {
  id: number;
  icone: string;
  titulo: string;
  descricao?: string;
  link?: string;
  cor_fundo?: string;
  cor_texto?: string;
  ordem: number;
}

export interface CMSPlanFeature {
  id: number;
  texto: string;
  incluido: boolean;
}

export interface CMSPlan {
  id: number;
  nome: string;
  subtitulo?: string;
  preco?: number;
  moeda?: string;
  intervalo?: string;
  badge?: string;
  btn_texto?: string;
  btn_link?: string;
  destaque: boolean;
  features: CMSPlanFeature[];
}

export interface CMSTestimonial {
  id: number;
  citacao: string;
  autor_nome: string;
  autor_cargo?: string;
  autor_empresa?: string;
  autor_avatar?: string;
  avaliacao: number;
}

export interface CMSFAQ {
  id: number;
  categoria?: string;
  pergunta: string;
  resposta: string;
}

export interface CMSPage {
  id: number;
  slug: string;
  titulo: string;
  meta_descricao?: string;
  meta_keywords?: string;
  hero?: CMSHero;
  features: CMSFeature[];
  pricing: CMSPlan[];
  testimonials: CMSTestimonial[];
  faq: CMSFAQ[];
}

export interface CMSBlogCategory {
  id: number;
  slug: string;
  nome: string;
  descricao?: string;
  cor?: string;
}

export interface CMSBlogAuthor {
  id: number;
  nome: string;
  cargo?: string;
  bio?: string;
  avatar?: string;
}

export interface CMSBlogPost {
  id: number;
  slug: string;
  titulo: string;
  resumo?: string;
  conteudo?: string;
  categoria_id?: number;
  categoria_nome?: string;
  categoria_slug?: string;
  categoria_cor?: string;
  autor_id?: number;
  autor_nome?: string;
  autor_cargo?: string;
  autor_avatar?: string;
  imagem_destaque?: string;
  tempo_leitura: number;
  destaque: boolean;
  tags?: string;
  meta_descricao?: string;
  visualizacoes: number;
  data_publicacao?: string;
}

export interface CMSFooterLink {
  id: number;
  texto: string;
  url: string;
  badge?: string;
  externo: boolean;
}

export interface CMSFooterColumn {
  id: number;
  titulo: string;
  links: CMSFooterLink[];
}

export interface CMSFooterSocial {
  id: number;
  rede: string;
  url: string;
  icone?: string;
}

export interface CMSFooter {
  descricao_empresa?: string;
  copyright_texto?: string;
  colunas: CMSFooterColumn[];
  social: CMSFooterSocial[];
}

export interface CMSConfig {
  [key: string]: string | undefined;
}

// =====================================================
// SERVICE
// =====================================================

class CMSService {
  /**
   * Get page content by slug
   */
  async getPage(slug: string): Promise<CMSPage> {
    const response = await api.get<{
      success: boolean;
      data?: CMSPage;
      error?: string;
    }>(`/public/cms/pages/${slug}`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao carregar página');
  }

  /**
   * Get blog posts
   */
  async getBlogPosts(params?: {
    status?: string;
    categoria?: string;
    destaque?: boolean;
    page?: number;
    limit?: number;
  }): Promise<{
    posts: CMSBlogPost[];
    pagination?: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    const response = await api.get<{
      success: boolean;
      data?: CMSBlogPost[];
      pagination?: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
      error?: string;
    }>('/public/cms/blog/posts', { params });

    if (response.data.success) {
      return {
        posts: response.data.data || [],
        pagination: response.data.pagination,
      };
    }

    throw new Error(response.data.error || 'Falha ao carregar artigos');
  }

  /**
   * Get featured blog posts
   */
  async getFeaturedPosts(limit: number = 3): Promise<CMSBlogPost[]> {
    const result = await this.getBlogPosts({ destaque: true, limit });
    return result.posts;
  }

  /**
   * Get recent blog posts
   */
  async getRecentPosts(limit: number = 6): Promise<CMSBlogPost[]> {
    const result = await this.getBlogPosts({ limit });
    return result.posts;
  }

  /**
   * Get blog post by slug
   */
  async getBlogPost(slug: string): Promise<CMSBlogPost> {
    const response = await api.get<{
      success: boolean;
      data?: CMSBlogPost;
      error?: string;
    }>(`/public/cms/blog/posts/${slug}`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao carregar artigo');
  }

  /**
   * Get blog categories
   */
  async getBlogCategories(): Promise<CMSBlogCategory[]> {
    const response = await api.get<{
      success: boolean;
      data?: CMSBlogCategory[];
      error?: string;
    }>('/public/cms/blog/categories');

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao carregar categorias');
  }

  /**
   * Get footer content
   */
  async getFooter(): Promise<CMSFooter> {
    const response = await api.get<{
      success: boolean;
      data?: CMSFooter;
      error?: string;
    }>('/public/cms/footer');

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao carregar footer');
  }

  /**
   * Get CMS config
   */
  async getConfig(): Promise<CMSConfig> {
    const response = await api.get<{
      success: boolean;
      data?: CMSConfig;
      error?: string;
    }>('/public/cms/config');

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error(response.data.error || 'Falha ao carregar configurações');
  }

  /**
   * Get global testimonials
   */
  async getTestimonials(): Promise<CMSTestimonial[]> {
    const response = await api.get<{
      success: boolean;
      data?: CMSTestimonial[];
      error?: string;
    }>('/public/cms/testimonials');

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    return [];
  }

  /**
   * Get global FAQ
   */
  async getFAQ(): Promise<CMSFAQ[]> {
    const response = await api.get<{
      success: boolean;
      data?: CMSFAQ[];
      error?: string;
    }>('/public/cms/faq');

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    return [];
  }
}

export default new CMSService();
